import type { AnswerInput, Question, QuestionnaireView } from '@/lib/api/types';
import { answerKey } from '@/lib/answerKey';

/**
 * The 20 core questions, flattened into the order the sales rep walks through them
 * (F-02: five layers, jurisdiction per layer, eight objectives, exit, resilience).
 *
 * The jurisdiction question covers five layers, so it occupies one screen and produces five
 * answers. Nothing here scores anything — it only decides what appears on which screen.
 */
export const JURISDICTION_TYPE = 'jurisdiktion';

export interface FlowStep {
  question: Question;
  sectionTitle: string;
  sectionIntro?: string | null;
  /** True for the jurisdiction screen, which collects one code per layer. */
  perLayer: boolean;
}

export function buildFlow(definition: QuestionnaireView): FlowStep[] {
  return definition.sections.flatMap((section) =>
    section.questions.map((question) => ({
      question,
      sectionTitle: section.title,
      sectionIntro: section.introText,
      perLayer: question.type === JURISDICTION_TYPE && question.perLayer === true,
    })),
  );
}

/** How many answers a step must produce before it counts as done. */
export function answersExpected(step: FlowStep, definition: QuestionnaireView): number {
  return step.perLayer ? definition.layers.length : 1;
}

export function isStepAnswered(
  step: FlowStep,
  definition: QuestionnaireView,
  answers: Record<string, AnswerInput>,
): boolean {
  if (!step.perLayer) {
    return Boolean(answers[answerKey(step.question.id)]);
  }

  return definition.layers.every((layer) =>
    Boolean(answers[answerKey(step.question.id, layer.id)]),
  );
}

/** Total answers the flow expects, used for the progress indicator. */
export function totalAnswers(flow: FlowStep[], definition: QuestionnaireView): number {
  return flow.reduce((sum, step) => sum + answersExpected(step, definition), 0);
}

export function answersGiven(
  flow: FlowStep[],
  definition: QuestionnaireView,
  answers: Record<string, AnswerInput>,
): number {
  return flow.reduce((sum, step) => {
    if (!step.perLayer) {
      return sum + (answers[answerKey(step.question.id)] ? 1 : 0);
    }

    return (
      sum +
      definition.layers.filter((layer) => answers[answerKey(step.question.id, layer.id)]).length
    );
  }, 0);
}
